// src/features/inventory/pages/ItemDetailPage/tabs/Value/SetCostPriceDialog.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { valuationAPI } from '../../../../services/valuation';
import { parseUnitCost } from '../../../../components/ReceiptCostFields';
import { useApi } from '../../../../../../hooks/api/useApi';
import { formatAmountInput } from '../../../../../../utils/formatters/currencyFormatter';
import InfoTip from '../../../../../../components/common/InfoTip';

/**
 * filledQuantity: stock on hand valued at an earlier item price because its
 * real cost was unknown. A new price leaves it alone (it is usually a price
 * change, and that stock was bought before it); the checkbox re-costs it
 * when the old price was simply wrong.
 */
export default function SetCostPriceDialog({
  open, onClose, onSuccess, itemId, uom, currentPrice, uncostedQuantity, filledQuantity = 0, inventoryTracked = true,
}) {
  const { t } = useTranslation(['inventory', 'common']);
  const { loading, error, execute, clearError } = useApi();
  const [price, setPrice] = useState('');
  const [recostFilled, setRecostFilled] = useState(false);

  useEffect(() => {
    if (open) {
      setPrice(formatAmountInput(currentPrice));
      setRecostFilled(false);
    }
  }, [open, currentPrice]);

  const parsed = parseUnitCost(price);
  const isValid = parsed != null;
  const canRecost = filledQuantity > 0 && parsed != null && parsed !== currentPrice;

  const handleClose = () => { clearError(); onClose(); };

  const handleSubmit = async () => {
    if (!isValid) return;
    const result = await execute(() => valuationAPI.setCostPrice(itemId, parsed, { recostFilled: canRecost && recostFilled }));
    if (result.success) { onSuccess(result.data.data); handleClose(); }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('inventory:items.tabs.value.cost_price_dialog.title')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
          {error && <Alert severity="error" onClose={clearError}>{error}</Alert>}

          <TextField
            label={t('inventory:items.tabs.value.cost_price_dialog.label', { uom })}
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            required
            autoFocus
            disabled={loading}
            slotProps={{
              input: { endAdornment: <InputAdornment position="end">{t('inventory:items.currency_suffix')}</InputAdornment> },
              htmlInput: { inputMode: 'decimal' },
            }}
            error={price !== '' && !isValid}
            helperText={price !== '' && !isValid
              ? t('inventory:items.receipt_cost.invalid')
              : t('inventory:items.tabs.value.incl_vat')}
          />

          <Typography variant="body2" color="text.secondary">
            {!inventoryTracked
              ? t('inventory:items.tabs.value.cost_price_dialog.untracked')
              : uncostedQuantity > 0
                ? t('inventory:items.tabs.value.cost_price_dialog.fills_uncosted', { count: uncostedQuantity, uom })
                : t('inventory:items.tabs.value.cost_price_dialog.future_only')}
          </Typography>

          {canRecost && (
            <FormControlLabel
              control={(
                <Checkbox
                  checked={recostFilled}
                  onChange={(e) => setRecostFilled(e.target.checked)}
                  disabled={loading}
                  size="small"
                />
              )}
              label={(
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                  {t('inventory:items.tabs.value.cost_price_dialog.recost_filled', { count: filledQuantity, uom })}
                  <InfoTip title={t('inventory:help.value.recost_filled')} />
                </Box>
              )}
              slotProps={{ typography: { variant: 'body2' } }}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          {t('common:buttons.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isValid || loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? t('common:buttons.saving') : t('common:buttons.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
